/**
 * The guide's wire: POST /v1/guide/turn streams named events (route, navigate, working, client_tool, awaiting, say,
 * done, error). One call per ask — or per batch of page-action results when a turn paused for the page.
 */
import { API_BASE_URL, authHeaders } from '@/cowork/omniad/api/coworkApi';

export interface GuideHereWire {
  page: string; label?: string; path?: string; context?: unknown;
  actions?: Array<{ name: string; does: string; args?: Record<string, string>; confirm?: boolean }>;
}
export interface GuideTurnBody {
  sessionId: string; mode: 'chat' | 'voice'; brandId?: string;
  here: GuideHereWire | null; pages: unknown[];
  text?: string; toolResults?: Array<{ id: string; result: unknown }>;
}
export type GuideEvent =
  | { event: 'route'; data: { page: string; p: number; aboutScreen?: number; ms: number } }
  | { event: 'navigate'; data: { page: string; params: Record<string, unknown>; inputs: Record<string, unknown>; by: 'router' | 'lykke'; ms: number } }
  | { event: 'working'; data: { tool: string } }
  | { event: 'client_tool'; data: { id: string; action: string; args: Record<string, unknown> } }
  | { event: 'awaiting'; data: { ids: string[] } }
  | { event: 'say'; data: { text: string; spoken: string } }
  | { event: 'done'; data: { ms: number; steps?: number } }
  | { event: 'error'; data: { message: string } };

/** Runs one turn and hands each event to `on` as it arrives. Resolves when the stream ends. */
export async function guideTurn(body: GuideTurnBody, on: (e: GuideEvent) => void, signal?: AbortSignal): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/v1/guide/turn`, { method: 'POST', headers: authHeaders(true), body: JSON.stringify(body), signal });
  if (!res.ok || !res.body) {
    const t = await res.text().catch(() => '');
    on({ event: 'error', data: { message: res.status === 401 ? 'Sign in again to talk to Lykke.' : t.slice(0, 160) || `Error ${res.status}` } });
    return;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let cut: number;
    while ((cut = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, cut); buf = buf.slice(cut + 2);
      const event = /^event: (.+)$/m.exec(chunk)?.[1]?.trim();
      const data = /^data: (.*)$/m.exec(chunk)?.[1];
      if (!event || data === undefined) continue;
      try { on({ event, data: JSON.parse(data) } as GuideEvent); } catch { /* a malformed line is skipped */ }
    }
  }
}

export const guideReset = (sessionId: string) =>
  fetch(`${API_BASE_URL}/v1/guide/reset`, { method: 'POST', headers: authHeaders(true), body: JSON.stringify({ sessionId }) });
